import { useEffect, type ReactNode } from 'react';
import { ApiError } from '../api/client';
import type { CustomField, Lead } from '../api/types';

/* -------------------------------- status -------------------------------- */

export function StatusChip({ status }: { status: Lead['status'] }) {
  // The colour is admin-configured, so the chip stays in sync with the CRM's
  // own settings without a frontend deploy. Tinting the background from the
  // same hue keeps contrast reasonable whatever they pick.
  return (
    <span
      className="chip"
      style={{ color: status.color, background: `${status.color}1F`, borderColor: `${status.color}55` }}
      title={`${status.name} · ${status.category}`}
    >
      <span className="dot" />
      {status.name}
    </span>
  );
}

/* -------------------------------- errors -------------------------------- */

export function ErrorBanner({ error }: { error: unknown }) {
  if (!error) return null;
  const isApi = error instanceof ApiError;
  const message = isApi ? error.message : error instanceof Error ? error.message : String(error);
  // requestId maps to the exact server log line — surfacing it turns a vague
  // bug report into a one-minute lookup.
  const requestId = isApi ? error.body.requestId : '';
  // "Request validation failed" alone says nothing; the server names each field.
  const details = isApi && error.code === 'validation_failed' ? (error.body.details ?? []) : [];
  const fieldName = (f: string) => f.replace(/^cf_/, '').replace(/[._]/g, ' ');

  return (
    <div className="banner banner-error" role="alert">
      <div>
        <div>{details.length ? 'Please check the form:' : message}</div>
        {details.length > 0 && (
          <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
            {details.map((d, i) => (
              <li key={i}>{d.label ?? fieldName(d.field)} {d.message}</li>
            ))}
          </ul>
        )}
        {requestId && <div className="rid">Request {requestId}</div>}
      </div>
    </div>
  );
}

/* -------------------------------- states -------------------------------- */

export function EmptyState({
  title,
  children,
  action,
}: {
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      <h3>{title}</h3>
      {children && <p>{children}</p>}
      {action && <div style={{ marginTop: 16 }}>{action}</div>}
    </div>
  );
}

export function TableSkeleton({ rows = 8, cols = 6 }: { rows?: number; cols?: number }) {
  return (
    <tbody>
      {Array.from({ length: rows }, (_, r) => (
        <tr key={r}>
          {Array.from({ length: cols }, (_, c) => (
            <td key={c}>
              <div className="skeleton" style={{ height: 13, width: `${45 + ((r * 7 + c * 13) % 45)}%` }} />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );
}

/* -------------------------------- modal -------------------------------- */

export function Modal({
  title,
  onClose,
  children,
  footer,
  width,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  width?: number;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    // Stop the page behind from scrolling while the dialog is open.
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose]);

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" role="dialog" aria-modal="true" aria-label={title} style={width ? { maxWidth: width } : undefined}>
        <div className="modal-head">
          <h2>{title}</h2>
          <button className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Close" style={{ marginLeft: 'auto' }}>
            ✕
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

/* ------------------------- custom field formatting ------------------------- */

/**
 * Renders a stored custom-field value for a human.
 *
 * Stored values are option *keys* — a lead holds "12th", which the admin may
 * display as "Class XII". Printing the raw value is a bug users notice
 * immediately, so every read path goes through here.
 */
export function displayCustomValue(
  apiKey: string,
  value: unknown,
  byKey: Map<string, CustomField>,
): string {
  const field = byKey.get(apiKey);
  if (value === null || value === undefined || value === '') return '—';
  if (!field) return String(value);

  switch (field.field_type) {
    case 'multi_select': {
      const values = Array.isArray(value) ? value : [value];
      return values
        .map((v) => field.options.find((o) => o.value === v)?.label ?? String(v))
        .join(', ');
    }
    case 'select':
    case 'radio':
      return field.options.find((o) => o.value === value)?.label ?? String(value);
    case 'boolean':
    case 'checkbox':
      return value ? 'Yes' : 'No';
    case 'date':
      return new Date(`${String(value)}T00:00:00`).toLocaleDateString();
    case 'datetime':
      return new Date(String(value)).toLocaleString();
    case 'decimal':
    case 'number':
      return Number(value).toLocaleString();
    default:
      return String(value);
  }
}

/** Chips for a multi_select, plain text otherwise. */
export function CustomValue({
  apiKey,
  value,
  byKey,
}: {
  apiKey: string;
  value: unknown;
  byKey: Map<string, CustomField>;
}) {
  const field = byKey.get(apiKey);
  if (field?.field_type === 'multi_select' && Array.isArray(value) && value.length > 0) {
    return (
      <>
        {value.map((v) => (
          <span className="tag" key={String(v)}>
            {field.options.find((o) => o.value === v)?.label ?? String(v)}
          </span>
        ))}
      </>
    );
  }
  return <>{displayCustomValue(apiKey, value, byKey)}</>;
}

/* -------------------------------- dates -------------------------------- */

export function relativeTime(iso: string | null): string {
  if (!iso) return '—';
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

export function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
